import type { QuestionStatus } from './constants';
import type { PublicReply } from './api';

/** Semantic tones shared by presentation metadata, independent of CSS names. */
export const PRESENTATION_TONES = [
  'neutral',
  'info',
  'success',
  'warning',
  'danger',
] as const;

export type PresentationTone = (typeof PRESENTATION_TONES)[number];

/** Customer and staff labels are separate registers over the same machine code. */
export interface PresentationMetadata {
  readonly customerLabel: string;
  readonly staffLabel: string;
  readonly tone: PresentationTone;
}

/**
 * Existing closed reply-designation vocabulary.
 *
 * This mirrors the current `replies.reply_type` machine codes without adding,
 * removing, or renaming a payload or database value.
 */
export const REPLY_DESIGNATIONS = [
  'GENERAL',
  'RFI_REQUEST',
  'RFI_ANSWER',
  'SOLUTION',
] as const;

export type ReplyDesignation = (typeof REPLY_DESIGNATIONS)[number];

/** Existing closed reply-author vocabulary. */
export type AuthorRole = PublicReply['userType'];

export const AUTHOR_ROLES = [
  'ADMIN',
  'CUSTOMER',
  'GUEST',
] as const satisfies readonly AuthorRole[];

/** Canonical presentation metadata for the existing question workflow states. */
export const QUESTION_STATUS_PRESENTATIONS = {
  PENDING_REVIEW: {
    customerLabel: 'Pending review',
    staffLabel: 'Pending Review',
    tone: 'warning',
  },
  OPEN: {
    customerLabel: 'Open',
    staffLabel: 'Open',
    tone: 'info',
  },
  IN_PROGRESS: {
    customerLabel: 'In progress',
    staffLabel: 'In Progress',
    tone: 'info',
  },
  NEEDS_MORE_INFO: {
    customerLabel: 'More information needed',
    staffLabel: 'Needs More Info',
    tone: 'warning',
  },
  RESOLVED: {
    customerLabel: 'Answered',
    staffLabel: 'Resolved',
    tone: 'success',
  },
  CLOSED_DUPLICATE: {
    customerLabel: 'Duplicate',
    staffLabel: 'Closed (Duplicate)',
    tone: 'neutral',
  },
  CLOSED_REJECTED: {
    customerLabel: 'Not published',
    staffLabel: 'Rejected',
    tone: 'danger',
  },
} as const satisfies Readonly<Record<QuestionStatus, PresentationMetadata>>;

/** Canonical presentation metadata for the existing reply designations. */
export const REPLY_DESIGNATION_PRESENTATIONS = {
  GENERAL: {
    customerLabel: 'Reply',
    staffLabel: 'General reply',
    tone: 'neutral',
  },
  RFI_REQUEST: {
    customerLabel: 'Information requested',
    staffLabel: 'RFI request',
    tone: 'warning',
  },
  RFI_ANSWER: {
    customerLabel: 'Information provided',
    staffLabel: 'RFI answer',
    tone: 'info',
  },
  SOLUTION: {
    customerLabel: 'Solution',
    staffLabel: 'Solution',
    tone: 'success',
  },
} as const satisfies Readonly<Record<ReplyDesignation, PresentationMetadata>>;

/** Canonical presentation metadata for the existing reply-author roles. */
export const AUTHOR_ROLE_PRESENTATIONS = {
  ADMIN: {
    customerLabel: 'Store staff',
    staffLabel: 'Administrator',
    tone: 'info',
  },
  CUSTOMER: {
    customerLabel: 'Customer',
    staffLabel: 'Customer',
    tone: 'neutral',
  },
  GUEST: {
    customerLabel: 'Customer',
    staffLabel: 'Guest',
    tone: 'neutral',
  },
} as const satisfies Readonly<Record<AuthorRole, PresentationMetadata>>;

/**
 * ADR-032's accepted English public editorial-correction disclosures.
 * English is the only locale currently supported by the Widget/static
 * renderer. Add a locale's equivalent neutral copy here before that locale is
 * enabled; do not translate or construct these labels ad hoc in a surface.
 */
export const EDITORIAL_CORRECTION_PRESENTATIONS = {
  CUSTOMER_OR_GUEST: 'Edited by staff',
  STAFF: 'Edited',
} as const;

/** Resolve public correction copy from the existing public author role. */
export function resolveEditorialCorrectionLabel(authorRole: unknown): string {
  return authorRole === 'ADMIN'
    ? EDITORIAL_CORRECTION_PRESENTATIONS.STAFF
    : EDITORIAL_CORRECTION_PRESENTATIONS.CUSTOMER_OR_GUEST;
}

export const UNKNOWN_QUESTION_STATUS_PRESENTATION = {
  customerLabel: 'Status unavailable',
  staffLabel: 'Unknown status',
  tone: 'neutral',
} as const satisfies PresentationMetadata;

export const UNKNOWN_REPLY_DESIGNATION_PRESENTATION = {
  customerLabel: 'Reply',
  staffLabel: 'Unknown reply designation',
  tone: 'neutral',
} as const satisfies PresentationMetadata;

export const UNKNOWN_AUTHOR_ROLE_PRESENTATION = {
  customerLabel: 'Contributor',
  staffLabel: 'Unknown author role',
  tone: 'neutral',
} as const satisfies PresentationMetadata;

function hasOwnStringKey<Mapping extends object>(
  mapping: Mapping,
  value: unknown,
): value is Extract<keyof Mapping, string> {
  return typeof value === 'string' && Object.prototype.hasOwnProperty.call(mapping, value);
}

/** Resolve unchecked input without exposing a raw machine value to customers. */
export function resolveQuestionStatusPresentation(value: unknown): PresentationMetadata {
  return hasOwnStringKey(QUESTION_STATUS_PRESENTATIONS, value)
    ? QUESTION_STATUS_PRESENTATIONS[value]
    : UNKNOWN_QUESTION_STATUS_PRESENTATION;
}

/** Resolve unchecked input without exposing a raw machine value to customers. */
export function resolveReplyDesignationPresentation(value: unknown): PresentationMetadata {
  return hasOwnStringKey(REPLY_DESIGNATION_PRESENTATIONS, value)
    ? REPLY_DESIGNATION_PRESENTATIONS[value]
    : UNKNOWN_REPLY_DESIGNATION_PRESENTATION;
}

/** Resolve unchecked input without exposing a raw machine value to customers. */
export function resolveAuthorRolePresentation(value: unknown): PresentationMetadata {
  return hasOwnStringKey(AUTHOR_ROLE_PRESENTATIONS, value)
    ? AUTHOR_ROLE_PRESENTATIONS[value]
    : UNKNOWN_AUTHOR_ROLE_PRESENTATION;
}
